import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Language {
  key: string;
  name: string;
  flag: string;
}

const LANGUAGE_SELECTOR_ID = "language-selector";

const DropdownLanguage = ({ isFooter = false }: { isFooter?: boolean }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const languages = [
    { key: "es-ES", name: "Español", flag: "es" },
    { key: "en-GB", name: "English", flag: "gb" },
    { key: "pt-PT", name: "Portugués", flag: "pt" },
    { key: "es-MX", name: "Español de México", flag: "mx" },
  ];

  const availableLanguages: { [key: string]: string } = {
    "es-ES": "es-ES",
    "en-GB": "en-GB",
    "es-MX": "es-MX",
    pt: "pt-PT",
    "pt-PT": "pt-PT",
    "pt-BR": "pt-PT",
  };

  const selectedLanguage = languages.find(
    (language) => language.key === availableLanguages[i18n.language]
  );

  const handleLanguageChange = async (language: Language) => {
    await i18n.changeLanguage(language.key);
    localStorage.setItem("lang", language.key);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleWindowClick = (event: any) => {
      const target = event.target.closest("button");
      if (target && target.id === LANGUAGE_SELECTOR_ID) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("click", handleWindowClick);
    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, []);

  if (!selectedLanguage) {
    return null;
  }

  return (
    <>
      <div className="flex items-center z-40">
        <div className="relative inline-block text-left">
          <div>
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="inline-flex items-center justify-center w-full rounded-xl border border-gray-200 px-4 py-2 bg-white text-sm font-medium text-gray-700"
              id={LANGUAGE_SELECTOR_ID}
              aria-haspopup="true"
              aria-expanded={isOpen}
            >
              <span
                className={`fi fis inline-block mr-2 fi-${selectedLanguage.flag}`}
              />
              {selectedLanguage.name}
              <svg
                className="-mr-1 ml-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L10 12.586l3.293-3.293a1 1 0 011.414 1.414l-4 4z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          {isOpen && (
            <div
              className={`${
                isFooter ? "bottom-10 right-2" : "top-8 right-0"
              } absolute mt-2 w-48 rounded-md bg-white ring-1 ring-black ring-opacity-5 ring-gray-700 text-gray-200`}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="language-selector"
            >
              <div className="py-1 grid grid-cols-1" role="none">
                {languages.map((language, index) => {
                  return (
                    <button
                      key={language.key}
                      onClick={() => handleLanguageChange(language)}
                      className={`${
                        selectedLanguage.key === language.key
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-700"
                      } block px-4 py-2 text-sm text-left items-center inline-flex hover:bg-gray-100 ${
                        index % 2 === 0 ? "rounded-r" : "rounded-l"
                      }`}
                      role="menuitem"
                    >
                      <span
                        className={`fi fis inline-block mr-2 fi-${language.flag}`}
                      />
                      <span className="truncate">{language.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { DropdownLanguage };
